import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Alert, Keyboard, ScrollView, StyleSheet, } from 'react-native';
import { SettingsItem, SettingsItemInput, SettingsItemNavigate, } from 'design-system/SettingsItem';
import { SettingsSection } from 'design-system/SettingsSection';
import { AppView } from 'design-system/AppView';
import { rem } from 'design-system/values';
import * as Icons from 'design-system/icons';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { formatAmount, formatDateForItem } from 'utils';
import { rruleStringToFrequency } from 'features/subscriptions/utils/frequency';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useColors from 'hooks/useColors';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import Modal from 'utils/packages/Modal';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { deleteTransactionSchedule, getTransactionsForSchedule, updateTransactionSchedule, } from 'actions';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import { Sign } from 'utils/types/navigation';
import { muteErrorAndFetching, useFetchOld } from 'hooks';
import { isWeb } from '../../../constants';
import IconTransactionCategory from '../icons/IconTransactionCategory';
import { selectTransactionScheduleById } from '../selectors';
const strings = {
    headerTitle: 'Scheduled transaction',
    reference: 'Reference',
    started: 'Started',
    frequency: 'Frequency',
    amount: 'Amount',
    category: 'Category',
    createdTransactions: 'Created transactions',
    modal: {
        saveChanges: {
            title: 'Save the changes',
            subtitle: 'These changes apply only to future scheduled transactions',
            confirmButtonTitle: 'Got it, let me save',
            cancelButtonTitle: 'Cancel',
        },
        deleteTransaction: {
            title: 'Delete scheduled transaction',
            subtitle: 'Are you sure you want to delete this scheduled transaction?',
            confirmButtonTitle: 'Delete',
            cancelButtonTitle: 'Cancel',
        },
    },
    infoCard: 'All changes will apply to future transactions and will not affect past transactions.',
    amountChangeText: 'What’s the amount for this transaction?',
};
const ScheduledTransactionScreen = withAnimatedNavigationHeader(({ navigation, onScroll, route }) => {
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const dispatch = useAppDispatch();
    const { currency, accountId, scheduleId } = route.params;
    const schedule = useAppSelector((store) => selectTransactionScheduleById(store, scheduleId));
    const { counterpartName = '', startDate = '', rrule = '', amount = 0, category = null, transactionIds = [], transactions, } = schedule || {};
    const initialValue = useRef({
        counterpartName,
        amount,
        category,
    });
    const [isEditing, setIsEditing] = useState(false);
    const [counterpartNameValue, setCounterpartNameValue] = useState(counterpartName);
    const [amountValue, setAmountValue] = useState(amount);
    const [categoryValue, setCategoryValue] = useState(category);
    const counterpartNameValueRef = useRef(null);
    const hasChanges = useMemo(() => counterpartNameValue !== initialValue.current.counterpartName ||
        amountValue !== initialValue.current.amount ||
        categoryValue !== initialValue.current.category, [counterpartNameValue, amountValue, categoryValue]);
    const onRevertChanges = useCallback(() => {
        setCounterpartNameValue(initialValue.current.counterpartName);
        setAmountValue(initialValue.current.amount);
        setCategoryValue(initialValue.current.category);
        setIsEditing(false);
    }, []);
    const onPressEdit = useCallback(() => {
        setIsEditing(true);
    }, []);
    const onCommitDelete = useCallback(() => {
        navigation.goBack();
        setIsEditing(false);
        dispatch(deleteTransactionSchedule({
            id: Number(scheduleId),
        }));
    }, [scheduleId]);
    const onPressDelete = useCallback(() => {
        Modal.showConfirmation({
            title: strings.modal.deleteTransaction.title,
            subtitle: strings.modal.deleteTransaction.subtitle,
            confirmButtonTitle: strings.modal.deleteTransaction.confirmButtonTitle,
            cancelButtonTitle: strings.modal.deleteTransaction.cancelButtonTitle,
            onConfirm: onCommitDelete,
            onPressCancel: onRevertChanges,
        });
    }, [onCommitDelete, onRevertChanges]);
    const onCommitSave = useCallback(async () => {
        await dispatch(updateTransactionSchedule({
            id: Number(scheduleId),
            counterpartName: counterpartNameValue,
            categoryId: categoryValue?.id,
            amount: amountValue,
        }));
        setIsEditing(false);
    }, [scheduleId, counterpartNameValue, categoryValue?.id, amountValue]);
    const onPressSave = useCallback(() => {
        if (!hasChanges) {
            setIsEditing(false);
        }
        else if (amountValue === 0) {
            Alert.alert('Hold on 💫', 'The amount cannot be 0.', undefined, {});
        }
        else if (!counterpartNameValue.trim().length) {
            Alert.alert('Hold on 💫', 'The Reference name cannot be empty.', [
                {
                    onPress: () => counterpartNameValueRef.current?.focus?.(),
                    text: 'OK',
                },
            ]);
        }
        else {
            Keyboard.dismiss();
            Modal.showConfirmation({
                title: strings.modal.saveChanges.title,
                subtitle: strings.modal.saveChanges.subtitle,
                confirmButtonTitle: strings.modal.saveChanges.confirmButtonTitle,
                cancelButtonTitle: strings.modal.saveChanges.cancelButtonTitle,
                onConfirm: onCommitSave,
                onPressCancel: onRevertChanges,
            });
        }
    }, [
        amountValue,
        counterpartNameValue,
        hasChanges,
        onCommitSave,
        onRevertChanges,
    ]);
    const onPressCategory = useCallback(() => {
        navigation.navigateDeprecated('EditCategory', {
            onSimplePickCategory(newCategory) {
                setCategoryValue(newCategory);
                navigation.goBack();
            },
        });
    }, [navigation]);
    const [initialTransactions, , , fetchInitialTxns] = useFetchOld(muteErrorAndFetching);
    useEffect(() => {
        let timeout;
        if (accountId &&
            scheduleId &&
            transactionIds &&
            !transactions?.length) {
            timeout = setTimeout(() => {
                fetchInitialTxns(getTransactionsForSchedule({
                    scheduleId,
                    transactionIds,
                    accountIdsStr: `accountIds[]=${accountId}`,
                }, 1));
            }, 200);
        }
        return () => clearTimeout(timeout);
    }, [
        accountId,
        fetchInitialTxns,
        scheduleId,
        transactionIds,
        transactions?.length,
    ]);
    const onPressTransactions = useCallback(() => {
        if (accountId && scheduleId && transactionIds) {
            navigation.navigateDeprecated('ScheduledTransactionList', {
                accountId,
                transactionIds,
                scheduleId,
                initialTransactions: initialTransactions?.transactions || transactions,
            });
        }
    }, [
        accountId,
        transactionIds,
        scheduleId,
        initialTransactions?.transactions,
        transactions,
    ]);
    const onEditAmount = useCallback(() => {
        navigation.navigateDeprecated('AddBalance', {
            custom: {
                title: strings.amount,
                icon: (<Icons.IconCoins height={32} width={32} alt2 gradientKey="darkPink"/>),
                hideCurrencyPicker: true,
                defaultValue: amountValue < 0 ? amountValue * -1 : amountValue,
                defaultCurrency: currency,
                initialSign: amountValue > 0 ? Sign.Positive : Sign.Negative,
                webSubtitle: strings.amountChangeText,
                webBottomSignSwap: true,
                zeroNotAllowed: true,
            },
            onContinue: (amount) => {
                setAmountValue(amount);
                navigation.goBack();
            },
        });
    }, [amountValue, currency]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => isEditing ? (<NavigationHeaderRightIconButton onPress={onPressDelete}>
                <Icons.IconTrash color={colors.buttons.primary}/>
              </NavigationHeaderRightIconButton>) : (<NavigationHeaderRightIconButton onPress={onPressEdit}>
                <Icons.IconEdit color={colors.buttons.primary}/>
              </NavigationHeaderRightIconButton>),
        });
    }, [colors.buttons.primary, isEditing, onPressDelete, onPressEdit]);
    return (<AppView withFrame={false}>
          <ScrollView onScroll={onScroll} style={paddingHorizontalStyle} scrollEventThrottle={16}>
            <SettingsSection noMarginTop style={styles.marginTop}>
              <SettingsItemInput description={strings.reference} ref={counterpartNameValueRef} placeholder={'Reference'} value={counterpartNameValue} onChangeText={setCounterpartNameValue} editable={isEditing} disabled={!isEditing} keyboardType="default" placeholderTextColor={colors.text.primary} icon={<Icons.IconText gradientKey="lightPurple"/>}/>
              <SettingsItem title={startDate ? formatDateForItem(startDate) : '-'} disabled topDescription={strings.started} onPress={() => { }} icon={<Icons.IconBoldCalendar gradientKey="darkPurple"/>}/>
              <SettingsItem title={rruleStringToFrequency(rrule) || '-'} disabled topDescription={strings.frequency} onPress={() => { }} icon={<Icons.IconArrowBack gradientKey="darkPink"/>}/>
              <SettingsItemNavigate title={<Text Numbers-16>
                    {formatAmount(amountValue, currency, 2)}
                  </Text>} description={strings.amount} disabled={!isEditing} onPress={onEditAmount} icon={<Icons.IconCoins alt gradientKey="darkPink"/>}/>
              {!!categoryValue && (<SettingsItemNavigate title={categoryValue?.displayName} description={strings.category} disabled={!isEditing} onPress={onPressCategory} icon={<IconTransactionCategory category={categoryValue}/>}/>)}
              {!isEditing && transactionIds && transactionIds.length > 0 ? (<SettingsItemNavigate title={transactionIds.length.toString()} description={strings.createdTransactions} onPress={onPressTransactions} icon={<Icons.IconRectangleListSolid gradientKey="apricot"/>}/>) : null}
            </SettingsSection>
            {!!isEditing && (<InfoCard style={styles.infoCard}>
                <Text TextThin-14 Secondary>
                  {strings.infoCard}
                </Text>
              </InfoCard>)}
          </ScrollView>
          {!!isEditing && (<KeyboardAvoidingButton brand onPress={onPressSave} title="Save"/>)}
        </AppView>);
}, ({ colors }) => ({
    title: strings.headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    marginTop: {
        marginTop: rem(16),
    },
    infoCard: {
        marginTop: rem(16),
    },
});
export default ScheduledTransactionScreen;
